import { useEffect, useMemo, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { fmtCFA } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { Download, TrendingUp, ShoppingBag, Users, UserCheck } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import VendeurFilter from "@/components/VendeurFilter";

const COLORS = { especes: "#10B981", wave: "#0EA5E9", orange_money: "#F97316", produit: "#10B981", seance: "#3B82F6", abonnement: "#A855F7" };
const moisLabel = (m) => new Date(`${m}-01T12:00:00`).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

export default function Rapports() {
  const { theme } = useTheme();
  const dark = theme === "dark";
  const [mois, setMois] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [vendeur, setVendeur] = useState("");

  useEffect(() => {
    setData(null);
    api.get("/rapports/mensuel", { params: { mois, ...(vendeur ? { vendeur_id: vendeur } : {}) } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [mois, vendeur]);

  const exporter = async () => {
    try {
      const r = await api.get("/rapports/mensuel/export", { params: { mois, ...(vendeur ? { vendeur_id: vendeur } : {}) }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rapport_${mois}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Rapport CSV téléchargé");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const tooltipStyle = useMemo(() => ({ contentStyle: { backgroundColor: dark ? "#131B2E" : "#fff", border: `1px solid ${dark ? "#1E293B" : "#E2E8F0"}`, borderRadius: 8 }, labelStyle: { color: dark ? "#F8FAFC" : "#0F172A" } }), [dark]);
  const paiementsNonNuls = useMemo(() => (data ? data.par_paiement.filter((p) => p.total > 0) : []), [data]);
  const parJourChart = useMemo(() => (data ? data.par_jour.map((j) => ({ ...j, jour: j.date.slice(8) })) : []), [data]);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="rapports-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">Rapport mensuel</h1>
          <p className="text-sm text-muted-foreground mt-1 capitalize">{moisLabel(mois)}{data?.vendeur_nom ? ` — ${data.vendeur_nom}` : ""}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <VendeurFilter value={vendeur} onChange={setVendeur} testid="rapport-vendeur-select" />
          <Input type="month" value={mois} onChange={(e) => e.target.value && setMois(e.target.value)} data-testid="rapport-mois-input" className="bg-background border-border text-foreground w-44" />
          <Button onClick={exporter} disabled={!data} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="rapport-export-button">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      {!data ? (
        <div className="text-muted-foreground text-sm animate-pulse" data-testid="rapport-loading">Chargement…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
            {[
              { label: "Chiffre d'affaires", value: fmtCFA(data.total), icon: TrendingUp, color: "text-emerald-600 dark:text-emerald-400", testid: "rapport-total" },
              { label: "Ventes", value: `${data.nb_ventes}`, sub: `panier moyen ${fmtCFA(data.panier_moyen)}`, icon: ShoppingBag, color: "text-blue-600 dark:text-blue-400", testid: "rapport-nb-ventes" },
              { label: "Présences", value: `${data.presences}`, sub: "passages enregistrés", icon: UserCheck, color: "text-amber-600 dark:text-amber-400", testid: "rapport-presences" },
              { label: "Nouveaux membres", value: `${data.nouveaux_membres}`, sub: `${data.nb_caisses} jour(s) de caisse`, icon: Users, color: "text-purple-600 dark:text-purple-400", testid: "rapport-nouveaux-membres" },
            ].map((s) => (
              <Card key={s.label} className="bg-card border-border" data-testid={s.testid}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">{s.label}</p>
                    <s.icon className={`w-4 h-4 ${s.color}`} />
                  </div>
                  <p className={`text-2xl font-display font-extrabold mt-2 ${s.color}`}>{s.value}</p>
                  {s.sub && <p className="text-xs text-muted-foreground mt-0.5">{s.sub}</p>}
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-card border-border" data-testid="rapport-paiement-card">
              <CardHeader className="pb-2"><CardTitle className="text-base font-semibold text-foreground">Par mode de paiement</CardTitle></CardHeader>
              <CardContent>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={150}>
                    <PieChart>
                      <Pie data={paiementsNonNuls} dataKey="total" nameKey="label" innerRadius={55} outerRadius={85} paddingAngle={3}>
                        {paiementsNonNuls.map((p) => <Cell key={p.paiement} fill={COLORS[p.paiement] || "#64748B"} />)}
                      </Pie>
                      <Tooltip formatter={(v) => fmtCFA(v)} {...tooltipStyle} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-1.5 mt-2">
                  {data.par_paiement.map((p) => (
                    <div key={p.paiement} className="flex items-center justify-between text-sm" data-testid={`rapport-paiement-${p.paiement}`}>
                      <span className="flex items-center gap-2 text-foreground/80"><span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[p.paiement] }} />{p.label} <span className="text-muted-foreground text-xs">({p.nb})</span></span>
                      <span className="font-semibold text-foreground">{fmtCFA(p.total)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border" data-testid="rapport-categorie-card">
              <CardHeader className="pb-2"><CardTitle className="text-base font-semibold text-foreground">Par catégorie</CardTitle></CardHeader>
              <CardContent>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={150}>
                    <BarChart data={data.par_categorie} layout="vertical" margin={{ left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={dark ? "#1E293B" : "#E2E8F0"} horizontal={false} />
                      <XAxis type="number" stroke="#64748B" fontSize={11} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                      <YAxis type="category" dataKey="label" stroke="#64748B" fontSize={12} width={90} />
                      <Tooltip formatter={(v) => fmtCFA(v)} {...tooltipStyle} />
                      <Bar dataKey="total" name="Total" radius={[0, 4, 4, 0]}>
                        {data.par_categorie.map((c) => <Cell key={c.type} fill={COLORS[c.type] || "#64748B"} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-1.5 mt-2">
                  {data.par_categorie.map((c) => (
                    <div key={c.type} className="flex items-center justify-between text-sm" data-testid={`rapport-categorie-${c.type}`}>
                      <span className="text-foreground/80">{c.label} <span className="text-muted-foreground text-xs">({c.quantite} article(s))</span></span>
                      <span className="font-semibold text-foreground">{fmtCFA(c.total)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-card border-border" data-testid="rapport-jour-card">
            <CardHeader className="pb-2"><CardTitle className="text-base font-semibold text-foreground">Ventes par jour</CardTitle></CardHeader>
            <CardContent className="h-64">
              {data.par_jour.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="rapport-empty">Aucune vente ce mois-ci</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={150}>
                  <BarChart data={parJourChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke={dark ? "#1E293B" : "#E2E8F0"} />
                    <XAxis dataKey="jour" stroke="#64748B" fontSize={11} />
                    <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                    <Tooltip formatter={(v) => fmtCFA(v)} {...tooltipStyle} />
                    <Bar dataKey="total" name="Total" fill="#10B981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card className="bg-card border-border" data-testid="rapport-top-card">
            <CardHeader className="pb-2"><CardTitle className="text-base font-semibold text-foreground">Top articles</CardTitle></CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="text-muted-foreground">#</TableHead>
                    <TableHead className="text-muted-foreground">Article</TableHead>
                    <TableHead className="text-muted-foreground">Catégorie</TableHead>
                    <TableHead className="text-muted-foreground text-right">Quantité</TableHead>
                    <TableHead className="text-muted-foreground text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.top_articles.length === 0 && (
                    <TableRow className="border-border"><TableCell colSpan={5} className="text-center text-muted-foreground py-6">Aucun article vendu</TableCell></TableRow>
                  )}
                  {data.top_articles.map((a, i) => (
                    <TableRow key={a.nom} className="border-border" data-testid={`rapport-top-${i}`}>
                      <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                      <TableCell className="font-medium text-foreground">{a.nom}</TableCell>
                      <TableCell><Badge variant="outline" className="border-border text-muted-foreground">{{ produit: "Nutrition", seance: "Séances", abonnement: "Abonnements" }[a.type] || a.type}</Badge></TableCell>
                      <TableCell className="text-right text-foreground/80">{a.quantite}</TableCell>
                      <TableCell className="text-right font-semibold text-emerald-600 dark:text-emerald-400">{fmtCFA(a.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
